import React, { useEffect } from "react";
import { Box, Typography, Container, Grid, Button, Link, List, ListItem, ListItemText } from "@mui/material";
import { motion } from "framer-motion";
import heroBg from "../../assets/nextix/business-authentication-banner.jpg";
import cityWideWhitepaper from '../../City-wide-whitepaper.pdf';
import globalVideoWhitepaper from '../../Global-Video-whitepaper.pdf';
import ipPhonesWhitepaper from '../../IP-Phones.pdf';
import ofwContactCenterWhitepaper from '../../OFW-Contact-Center.pdf';

const AuthenticationScreen = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box sx={{ backgroundColor: "#fff" }}>
            {/* BANNER IMAGE - Full image display in container */}
            <Container maxWidth="lg" sx={{ pt: 12, pb: 4 }}>
                <Box sx={{
                    borderRadius: "8px",
                    overflow: "hidden"
                }}>
                    <img
                        src={heroBg}
                        alt="Authentication - Secure Identity Verification"
                        style={{
                            width: "100%",
                            height: "auto",
                            display: "block"
                        }}
                    />
                </Box>
            </Container>

            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={{ xs: 2, md: 6 }}>
                    {/* Left Column - Main Content */}
                    <Grid item xs={12} md={8}>
                        {/* Main Authentication Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                Authentication
                            </Typography>
                        </motion.div>

                        {/* OVCODE */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                OVCODE
                            </Typography>
                            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 2, textAlign: "start" }}>
                                Ovcode helps you securely share, verify and save your trusted files. Ovcode utilizes the following technologies:
                            </Typography>
                            <List sx={{ listStyleType: 'decimal', pl: 4, mb: 4, color: "#666" }}>
                                <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primaryTypographyProps={{ sx: { lineHeight: 1.8 } }} primary="Blockchain Identity – establish data identity on blockchain" /></ListItem>
                                <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primaryTypographyProps={{ sx: { lineHeight: 1.8 } }} primary="Digital Twinning – track change and history of data" /></ListItem>
                                <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primaryTypographyProps={{ sx: { lineHeight: 1.8 } }} primary="Permanent Storage – store vital data in permanent storage" /></ListItem>
                                <ListItem sx={{ display: 'list-item', py: 0 }}><ListItemText primaryTypographyProps={{ sx: { lineHeight: 1.8 } }} primary="Artificial Intelligence – receive realtime and predictive data insight" /></ListItem>
                            </List>
                        </motion.div>

                        {/* OV-i */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                OV-i
                            </Typography>
                            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 4, textAlign: "start" }}>
                                OV-i or Ovcode Verifiable image Verification is the world’s first multi-patented technology that enable visual verification integrated with Blockchain technology to produce an immutable, secure product identification and authentication. OV-i can also be used as a marketing tool that features sophisticated, dynamic and interactive content.
                            </Typography>
                        </motion.div>

                        {/* Gemini */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                Gemini
                            </Typography>
                            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 4, textAlign: "start" }}>
                                Gemini is Ovcode’s Artificial Intelligence platform that provides matching of counterfeit documents or items from the original control files. Gemini also derives powerful insight from users’ published data in the OVCODE Infrastructure. Gemini integrates with big data management and machine learning to help support crucial decisions for sustainability and growth.
                            </Typography>
                        </motion.div>
                    </Grid>

                    {/* Right Column - Sidebar */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: "sticky" }, top: 100, textAlign: 'start' }}>
                            {/* Send Us A Message */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#375ca8" }}>
                                    Send Us A Message
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}>
                                    For more inquiries about our Products, send us a message
                                </Typography>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    href="mailto:info@nextix.co"
                                    sx={{
                                        backgroundColor: "#82c9ed",
                                        color: "white",
                                        fontWeight: 700,
                                        py: 1.5,
                                        mb: 4,
                                        "&:hover": { backgroundColor: "#6bb5d9" }
                                    }}
                                >
                                    CONTACT OUR SALES NOW
                                </Button>
                            </motion.div>

                            {/* Contact Information */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    Email Us:
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Inquiry:</strong>{" "}
                                        <Link href="mailto:info@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            info@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Support:</strong>{" "}
                                        <Link href="mailto:helpdesk@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            helpdesk@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Privacy:</strong>{" "}
                                        <Link href="mailto:dpo@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            dpo@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                {/* White Paper Resources */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    White Paper
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Link href={ofwContactCenterWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        OFW-Contact-Center
                                    </Link>
                                    <Link href={globalVideoWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        Global-Video-whitepaper
                                    </Link>
                                    <Link href={cityWideWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        City-wide-whitepaper
                                    </Link>
                                    {/* <Link href={ipPhonesWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", bgcolor: '#5bc0de' } }}>
                                        IP-Phones
                                    </Link> */}
                                </Box>
                            </motion.div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default AuthenticationScreen;
